import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountChip } from '../account/AccountChip';
import { PrivacyNote, type PrivacyState } from '../components/PrivacyNote/PrivacyNote';
import { BrandLockup } from './branding';
import './AppHeader.css';

export interface AppHeaderProps {
  /** Sub-brand label like "Studio" or "Gallery" — keeps the mark
   *  consistent while still signposting which surface the user is
   *  on. */
  subBrand?: string;
  /** Privacy chip state. Defaults to "on-device". */
  privacy?: PrivacyState;
  /** A short "← Back to X" CTA shown next to the brand. Pass `null`
   *  to hide. */
  back?: { label: string; to: string } | null;
  /** Slot for page-specific tools (e.g. a "Published" link on the
   *  studio home). Rendered between Privacy and AccountChip. */
  rightSlot?: ReactNode;
}

/**
 * Shared top chrome for every signed-in surface that isn't the
 * landing page or the admin console.
 *
 * Goal: a visitor should be able to land on `/gallery/<id>` from a
 * shared link and immediately know "I'm on BytaApps". Before this
 * component, the gallery had a back button + privacy chip and
 * nothing else.
 */
export function AppHeader({
  subBrand,
  privacy = 'on-device',
  back,
  rightSlot,
}: AppHeaderProps): JSX.Element {
  const navigate = useNavigate();
  return (
    <header className="bl-appheader" data-testid="app-header">
      <div className="bl-appheader__left">
        <BrandLockup subBrand={subBrand} />
        {back && (
          <button type="button" className="bl-appheader__back" onClick={() => navigate(back.to)}>
            ← {back.label}
          </button>
        )}
      </div>
      <div className="bl-appheader__right">
        <PrivacyNote state={privacy} />
        {rightSlot}
        <AccountChip />
      </div>
    </header>
  );
}
